import React, { useEffect, useState } from 'react';
import { Loader } from 'lucide-react';
import './Confirming.css';

function Confirming() {
  // ========================================
  // 1. OBTENER orderId (desde localStorage - lo guardó PaymentPage)
  // ========================================
  const [orderId] = useState(() => {
    const saved = localStorage.getItem('currentPayment');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        return data.orderId;
      } catch (e) {
        console.error('Error parseando currentPayment:', e);
      }
    }
    return null;
  });

  // Timestamp de cuándo llegó a esta página
  const [arrivedAt] = useState(Date.now());

  // ========================================
  // 2. ESTADO del pago y progreso
  // ========================================
  const [status, setStatus] = useState('confirming');
  const [progress, setProgress] = useState(0);
  const [canRedirect, setCanRedirect] = useState(false);

  // ========================================
  // 3. URLs
  // ========================================
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // ========================================
  // 4. PERMITIR REDIRECCIÓN DESPUÉS DE 8 SEGUNDOS
  // ========================================
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('⏰ Han pasado 8 segundos, permitiendo redirección a /success');
      setCanRedirect(true);
    }, 8000); // 8 segundos mínimo en esta pantalla

    return () => clearTimeout(timer);
  }, []);

  // ========================================
  // 5. POLLING: Consultar estado cada 3 segundos
  // ========================================
  useEffect(() => {
    if (!orderId) {
      console.error('❌ No hay orderId, redirigiendo a cancel');
      setTimeout(() => {
        window.location.href = '/cancel';
      }, 2000);
      return;
    }

    console.log('🔍 Confirming.js - Consultando orden:', orderId);

    const checkStatus = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/check-payment/${orderId}`);
        const data = await res.json();

        console.log('📊 Estado recibido:', data);
        
        // Actualizar estado visual
        if (data.status === 'confirmed' || data.payment_status === 'finished') {
          setStatus('confirmed');
          
          // Solo redirigir si ya pasaron 8 segundos
          if (canRedirect) {
            console.log('✅ PAGO CONFIRMADO y tiempo mínimo cumplido - Redirigiendo a /success');
            // 🔥 Preservar orderId para Success.js (que también lo pasará a /api/payment-complete).
            // Antes se borraba currentPayment aquí → Success leía null y mandaba "unknown".
            try {
              sessionStorage.setItem('lastConfirmedOrderId', orderId || '');
            } catch (e) {}
            localStorage.removeItem('currentPayment');
            window.location.href = '/success';
          } else {
            const timeLeft = 8000 - (Date.now() - arrivedAt);
            console.log(`⏳ Pago confirmado, esperando ${Math.ceil(timeLeft / 1000)}s más antes de redirigir...`);
          }
        } else if (data.status === 'waiting' || data.payment_status === 'waiting') {
          setStatus('confirming');
        } else if (data.status === 'expired' || data.payment_status === 'expired') {
          console.log('⏰ PAGO EXPIRADO - Redirigiendo a /cancel');
          localStorage.removeItem('currentPayment');
          window.location.href = '/cancel';
       } else if (data.status === 'failed' || data.payment_status === 'failed') {
          setStatus('failed');
          
          if (canRedirect) {
            console.log('❌ PAGO FALLIDO y tiempo mínimo cumplido - Redirigiendo a /cancel');
            localStorage.removeItem('currentPayment');
            window.location.href = '/cancel';
          } else {
            const timeLeft = 8000 - (Date.now() - arrivedAt);
            console.log(`⏳ Pago fallido, esperando ${Math.ceil(timeLeft / 1000)}s más antes de redirigir...`);
          }
        }
      } catch (err) {
        console.error('⚠️ Error polling:', err);
      }
    };

    // Primera verificación inmediata
    checkStatus();
    
    // Luego cada 3 segundos
    const interval = setInterval(checkStatus, 3000);
    
    return () => clearInterval(interval);
  }, [orderId, backendUrl, canRedirect, arrivedAt]);

  // ========================================
  // 6. BARRA DE PROGRESO
  // ========================================
  useEffect(() => {
  const startTime = Date.now();
  const totalDuration = 20 * 1000; // 20 segundos

  const update = () => {
    const elapsed = Date.now() - startTime;
    const newProgress = (elapsed / totalDuration) * 100;

    setProgress(Math.min(newProgress, 100));

    if (elapsed < totalDuration) {
      requestAnimationFrame(update);
    }
  };

  requestAnimationFrame(update);
}, []);

  // ========================================
  // 7. MENSAJES DE ESTADO
  // ========================================
  const statusMessages = {
    'waiting': 'Esperando confirmación...',
    'confirming': 'Confirmando en la red...',
    'confirmed': 'Confirmado! Redirigiendo...',
    'default': 'Procesando pago...'
  };

  const displayMessage = statusMessages[status] || statusMessages['default'];

  // ========================================
  // 8. RENDER
  // ========================================
  return (
    <div className="confirming-container">
      <div className="confirming-card">
        {/* Ícono con spinner */}
        <div className="confirming-icon-container">
          <Loader className="confirming-spinner" />
        </div>

        {/* Título */}
        <h1 className="confirming-title">Tramitación en curso</h1>

        {/* Texto principal */}
        <p className="confirming-text">
          Por favor, <strong>no cierres esta pestaña</strong> hasta que cargue la confirmación
        </p>

        {/* Estado actual */}
        <p className="confirming-text" style={{ fontSize: '13px', margin: '8px 0' }}>
          <strong>Estado:</strong> {displayMessage}
        </p>

        {/* Barra de progreso */}
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Pie de página */}
        <p className="confirming-text" style={{ fontSize: '12px', marginTop: '16px' }}>
          Puede tomar 1-10 minutos según la red
        </p>

        {orderId && (
          <p className="confirming-text" style={{ fontSize: '11px', opacity: 0.6, marginTop: '8px' }}>
            Order ID: {orderId}
          </p>
        )}
      </div>
    </div>
  );
}

export default Confirming;